import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { resolveApiMediaUrl } from '@/lib/media';
import { productCardStyles } from './ProductCard.styles';

export type ProductCardProps = {
  imageUrl: string;
  title: string;
  price: ReactNode;
  badge?: ReactNode;
  action?: ReactNode;
  onClick?: () => void;
  className?: string;
};

export function ProductCard({
  imageUrl,
  title,
  price,
  badge,
  action,
  onClick,
  className,
}: ProductCardProps): JSX.Element {
  const resolvedImageUrl = resolveApiMediaUrl(imageUrl) ?? imageUrl;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  const stopActionClick = (event: MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <div
      className={cn(
        productCardStyles.root,
        onClick && productCardStyles.clickable,
        className,
      )}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className={productCardStyles.imageWrap}>
        <img src={resolvedImageUrl} alt={title} className={productCardStyles.image} loading="lazy" />
      </div>
      <div className={productCardStyles.body}>
        <h3 className={productCardStyles.title}>{title}</h3>
        <div className={productCardStyles.meta}>
          <div>{price}</div>
          {badge ? <div>{badge}</div> : null}
        </div>
        {action ? (
          <div className={productCardStyles.action} onClick={stopActionClick}>
            {action}
          </div>
        ) : null}
      </div>
    </div>
  );
}
